import { useState, useEffect } from 'react';
import { 
  Leaf, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Loader2,
  FileText,
  Building2,
  ExternalLink,
  Clock,
  AlertTriangle,
  Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SustainabilityManager = () => {
  const [data, setData] = useState({ field_edits: [], pending_evidence: [], total_pending: 0 });
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);
  const [reviewDialog, setReviewDialog] = useState({ open: false, type: null, item: null });
  const [adminNotes, setAdminNotes] = useState('');

  const fetchPending = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/sustainability/pending`);
      if (!response.ok) throw new Error('Failed to fetch');
      const result = await response.json();
      setData(result);
    } catch (err) {
      toast.error('Failed to load pending items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPending();
  }, []);

  const handleReview = async (status) => {
    const { type, item } = reviewDialog;
    
    // Require feedback for rejection
    if (status === 'rejected' && !adminNotes.trim()) {
      toast.error('Please provide feedback when requesting changes');
      return;
    }
    
    setReviewing(item.id);
    
    try {
      let url;
      if (type === 'field_edit') {
        url = `${API_URL}/api/admin/sustainability/edits/${item.id}/review`;
      } else {
        url = `${API_URL}/api/admin/sustainability/evidence/${item.platform_id}/${item.id}/review`;
      }
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, admin_notes: adminNotes || null })
      });
      
      if (!response.ok) throw new Error('Failed to review');
      
      const result = await response.json();
      
      if (result.email_sent) {
        toast.success(`Item ${status === 'approved' ? 'approved' : 'rejected'} - vendor notified by email`);
      } else {
        toast.success(`Item ${status === 'approved' ? 'approved' : 'rejected'}`);
      }
      
      setReviewDialog({ open: false, type: null, item: null });
      setAdminNotes('');
      fetchPending();
    } catch (err) {
      toast.error('Failed to process review');
    } finally {
      setReviewing(null);
    }
  };

  const openReviewDialog = (type, item) => {
    setReviewDialog({ open: true, type, item });
    setAdminNotes('');
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground mt-2">Loading sustainability reviews...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Leaf className="w-5 h-5 text-green-600" />
            Sustainability Reviews
          </h2>
          <p className="text-sm text-muted-foreground">
            Review vendor-submitted sustainability information and evidence
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data.total_pending > 0 && (
            <Badge variant="secondary" className="bg-amber-100 text-amber-700">
              {data.total_pending} pending
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={fetchPending}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {data.total_pending === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-4" />
            <p className="text-lg font-medium">All caught up!</p>
            <p className="text-sm text-muted-foreground">
              No pending sustainability reviews
            </p>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="field_edits" className="space-y-4">
          <TabsList>
            <TabsTrigger value="field_edits" className="gap-2">
              <FileText className="w-4 h-4" />
              Field Updates
              {data.field_edits.length > 0 && (
                <Badge variant="secondary" className="ml-1">{data.field_edits.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="evidence" className="gap-2">
              <Building2 className="w-4 h-4" />
              Evidence
              {data.pending_evidence.length > 0 && (
                <Badge variant="secondary" className="ml-1">{data.pending_evidence.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Field Updates Tab */}
          <TabsContent value="field_edits" className="space-y-4">
            {data.field_edits.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <FileText className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No pending field updates</p>
                </CardContent>
              </Card>
            ) : (
              data.field_edits.map(edit => (
                <Card key={edit.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{edit.platform_name}</CardTitle>
                        <CardDescription>{edit.vendor_name}</CardDescription>
                      </div>
                      <Badge variant="outline" className="bg-amber-50 text-amber-700">
                        <Clock className="w-3 h-3 mr-1" />
                        Pending
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-muted/50 rounded-lg p-4">
                      <p className="text-xs font-medium text-muted-foreground mb-2">Proposed Changes:</p>
                      <div className="space-y-2 text-sm">
                        {Object.entries(edit.field_changes || {}).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-muted-foreground">{key.replace(/_/g, ' ')}:</span>
                            <span className="font-medium">
                              {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : 
                               Array.isArray(value) ? value.join(', ') : 
                               String(value).substring(0, 50)}
                              {String(value).length > 50 && '...'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => openReviewDialog('field_edit', edit)}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => openReviewDialog('field_edit', edit)}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Evidence Tab */}
          <TabsContent value="evidence" className="space-y-4">
            {data.pending_evidence.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Building2 className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No pending evidence reviews</p>
                </CardContent>
              </Card>
            ) : (
              data.pending_evidence.map(evidence => (
                <Card key={evidence.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{evidence.certificate_name}</CardTitle>
                        <CardDescription>
                          Platform: {evidence.platform_name}
                        </CardDescription>
                      </div>
                      <Badge variant="outline" className="bg-amber-50 text-amber-700">
                        <Clock className="w-3 h-3 mr-1" />
                        Pending
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Category:</span>
                        <span className="ml-2 font-medium capitalize">{evidence.category}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Scope:</span>
                        <span className="ml-2 font-medium">
                          {evidence.scope === 'platform-specific' ? 'Platform-specific' : 'Organisation-wide'}
                        </span>
                      </div>
                      {evidence.issuing_organisation && (
                        <div>
                          <span className="text-muted-foreground">Issuer:</span>
                          <span className="ml-2 font-medium">{evidence.issuing_organisation}</span>
                        </div>
                      )}
                      {evidence.date_issued && (
                        <div>
                          <span className="text-muted-foreground">Issued:</span>
                          <span className="ml-2 font-medium">{evidence.date_issued}</span>
                        </div>
                      )}
                    </div>
                    
                    {evidence.file_url && (
                      <a
                        href={evidence.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-primary hover:underline"
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        View Document
                      </a>
                    )}
                    
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => openReviewDialog('evidence', evidence)}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => openReviewDialog('evidence', evidence)}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Review Dialog */}
      <Dialog open={reviewDialog.open} onOpenChange={(open) => setReviewDialog({ ...reviewDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Submission</DialogTitle>
            <DialogDescription>
              {reviewDialog.type === 'field_edit' 
                ? 'Review sustainability field updates'
                : 'Review sustainability evidence'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm font-medium mb-2">
                {reviewDialog.item?.certificate_name || reviewDialog.item?.platform_name}
              </p>
              {reviewDialog.type === 'field_edit' && reviewDialog.item?.field_changes && (
                <div className="space-y-1 text-xs text-muted-foreground">
                  {Object.entries(reviewDialog.item.field_changes).map(([key, value]) => (
                    <p key={key}>
                      <strong>{key.replace(/_/g, ' ')}:</strong> {String(value).substring(0, 100)}
                    </p>
                  ))}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>
                Admin Notes 
                <span className="text-muted-foreground text-xs ml-1">
                  (required when requesting changes)
                </span>
              </Label>
              <Textarea
                placeholder="Provide feedback for the vendor..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                This feedback will be sent to the vendor by email when you request changes.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setReviewDialog({ open: false, type: null, item: null })}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              className="text-destructive hover:text-destructive"
              disabled={reviewing === reviewDialog.item?.id}
              onClick={() => handleReview('rejected')}
            >
              {reviewing === reviewDialog.item?.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-1" />
                  Reject
                </>
              )}
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              disabled={reviewing === reviewDialog.item?.id}
              onClick={() => handleReview('approved')}
            >
              {reviewing === reviewDialog.item?.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Approve
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SustainabilityManager;
